import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BarChart3, Clock, Trophy, Loader2, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BandejaEstadisticasPage() {
  const [dias, setDias] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/bandeja-unificada/estadisticas?dias=${dias}`);
        setStats(r.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [dias]);

  return (
    <div className="space-y-4" data-testid="bandeja-stats-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-600" /> Estadísticas de la Bandeja
          </h1>
          <p className="text-sm text-gray-600">Volumen, tiempos de respuesta y ranking de gestión.</p>
        </div>
        <select value={dias} onChange={e => setDias(parseInt(e.target.value))} className="border rounded px-3 py-2 text-sm" data-testid="select-dias">
          <option value={7}>Últimos 7 días</option>
          <option value={30}>Últimos 30 días</option>
          <option value={90}>Últimos 90 días</option>
          <option value={365}>Último año</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" /></div>
      ) : !stats ? (
        <p className="text-sm text-gray-500 italic">Sin datos disponibles.</p>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Tiempo primera respuesta */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-600 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Tiempo primera respuesta
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats.tiempo_primera_respuesta?.promedio_horas != null ? (
                  <>
                    <p className="text-3xl font-bold text-purple-700" data-testid="stat-tprom">
                      {stats.tiempo_primera_respuesta.promedio_horas}h
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Mediana: {stats.tiempo_primera_respuesta.mediana_horas}h · Muestra: {stats.tiempo_primera_respuesta.muestra}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-gray-500 italic">Sin datos suficientes</p>
                )}
              </CardContent>
            </Card>

            {/* Total volumen */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-600 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Conversaciones totales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-purple-700" data-testid="stat-total-conv">
                  {(stats.por_cuenta || []).reduce((acc, c) => acc + c.total_conversaciones, 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">En últimos {stats.dias_analizados} días</p>
              </CardContent>
            </Card>

            {/* Resueltas */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-600 flex items-center gap-2">
                  <Trophy className="w-4 h-4" /> Resueltas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-emerald-700" data-testid="stat-resueltas">
                  {(stats.por_cuenta || []).reduce((acc, c) => acc + (c.resueltas || 0), 0)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Estado = resuelto</p>
              </CardContent>
            </Card>
          </div>

          {/* Volumen por cuenta */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Volumen por cuenta</CardTitle>
            </CardHeader>
            <CardContent>
              {(stats.por_cuenta || []).length === 0 ? (
                <p className="text-sm text-gray-500 italic">Sin conversaciones en el período.</p>
              ) : (
                <div className="space-y-2" data-testid="volumen-por-cuenta">
                  {stats.por_cuenta.map(c => {
                    const max = Math.max(...stats.por_cuenta.map(x => x.total_conversaciones), 1);
                    const pct = (c.total_conversaciones / max) * 100;
                    return (
                      <div key={c.account_id} className="flex items-center gap-3">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: c.account_color || '#7c3aed' }}></span>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-baseline text-sm gap-2">
                            <span className="font-medium truncate">{c.account_label}</span>
                            <span className="text-gray-600 text-xs">
                              {c.total_conversaciones} conv · {c.sin_leer} sin leer · {c.resueltas} resueltas
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded overflow-hidden">
                            <div className="h-full" style={{ width: `${pct}%`, background: c.account_color || '#7c3aed' }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ranking */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Trophy className="w-4 h-4" /> Top 10 – Conversaciones resueltas por administrativa
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(stats.ranking_resueltas || []).length === 0 ? (
                <p className="text-sm text-gray-500 italic">Sin conversaciones resueltas en el período.</p>
              ) : (
                <ol className="space-y-1.5" data-testid="ranking-resueltas">
                  {stats.ranking_resueltas.map((r, i) => (
                    <li key={r.responsable_id} className="flex items-center gap-3 py-1 border-b last:border-b-0">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-800' : i === 1 ? 'bg-gray-100 text-gray-700' : i === 2 ? 'bg-amber-100 text-amber-800' : 'bg-slate-50 text-slate-500'}`}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {r.responsable_snapshot?.nombre || r.responsable_snapshot?.email || r.responsable_id}
                        </p>
                        {r.responsable_snapshot?.email && <p className="text-xs text-gray-500 truncate">{r.responsable_snapshot.email}</p>}
                      </div>
                      <span className="text-sm font-semibold text-purple-700">{r.resueltas}</span>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
